import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Save, Loader2, RotateCcw, Palette, Mail, Settings as SettingsIcon } from "lucide-react";

const SECTIONS = [
  { id: "branding", label: "Branding", icon: Palette, keys: ["site_name", "logo_url", "brand_primary", "brand_accent"] },
  { id: "platform", label: "Platform", icon: SettingsIcon, keys: ["default_exam_duration_min", "allow_signup"] },
  { id: "email", label: "Email templates", icon: Mail, keys: ["welcome_email_subject", "welcome_email_body", "announcement_email_subject", "announcement_email_body"] },
];

const isBooleanKey = (k) => k === "allow_signup";
const isColorKey = (k) => k === "brand_primary" || k === "brand_accent";
const isTextareaKey = (k) => k.endsWith("_body");

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [descriptions, setDescriptions] = useState({});
  const [publicity, setPublicity] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/settings");
    const s = {}, d = {}, p = {};
    data.forEach((row) => { s[row.key] = row.value; d[row.key] = row.description; p[row.key] = row.public; });
    setSettings(s); setDescriptions(d); setPublicity(p);
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    setErr(""); setMsg(""); setSavingKey(key);
    try {
      await api.put("/settings", { key, value: settings[key] ?? "", public: !!publicity[key], description: descriptions[key] || "" });
      setMsg(`Saved '${key}'.`);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setSavingKey(null); }
  };

  const reset = async (key) => {
    if (!window.confirm(`Reset '${key}' to default?`)) return;
    await api.delete(`/settings/${key}`);
    load();
    setMsg(`'${key}' restored to default.`);
  };

  const update = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  return (
    <AppShell title="Platform settings" subtitle="Branding, defaults and email templates for the whole institution.">
      {msg && (
        <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2" data-testid="settings-banner">
          {msg}
        </div>
      )}
      {err && (
        <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2" data-testid="settings-error">
          {err}
        </div>
      )}

      <div className="space-y-6">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.id} className="qs-card p-6" data-testid={`settings-section-${section.id}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="font-display text-lg font-bold text-slate-900">{section.label}</h2>
              </div>
              <div className="space-y-5">
                {section.keys.map((key) => (
                  <div key={key} className="flex flex-col md:flex-row md:items-start gap-3" data-testid={`setting-row-${key}`}>
                    <div className="md:w-56 shrink-0">
                      <label className="block font-semibold text-slate-800 text-sm">{key}</label>
                      <p className="text-xs text-slate-500 mt-0.5">{descriptions[key] || ""}</p>
                    </div>
                    <div className="flex-1 flex items-start gap-2">
                      {isBooleanKey(key) ? (
                        <select
                          value={settings[key] ?? "true"}
                          onChange={(e) => update(key, e.target.value)}
                          className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                          data-testid={`setting-input-${key}`}
                        >
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </select>
                      ) : isColorKey(key) ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="color"
                            value={settings[key] || "#4f46e5"}
                            onChange={(e) => update(key, e.target.value)}
                            className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                            data-testid={`setting-color-${key}`}
                          />
                          <input
                            type="text"
                            value={settings[key] ?? ""}
                            onChange={(e) => update(key, e.target.value)}
                            className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
                            data-testid={`setting-input-${key}`}
                          />
                        </div>
                      ) : isTextareaKey(key) ? (
                        <textarea
                          value={settings[key] ?? ""}
                          onChange={(e) => update(key, e.target.value)}
                          rows={4}
                          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-xs"
                          data-testid={`setting-input-${key}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={settings[key] ?? ""}
                          onChange={(e) => update(key, e.target.value)}
                          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                          data-testid={`setting-input-${key}`}
                        />
                      )}
                      <button
                        onClick={() => save(key)}
                        disabled={savingKey === key}
                        className="btn-primary text-xs shrink-0"
                        data-testid={`setting-save-${key}`}
                      >
                        {savingKey === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                      </button>
                      <button
                        onClick={() => reset(key)}
                        title="Reset to default"
                        className="text-slate-500 hover:text-slate-800 p-2"
                        data-testid={`setting-reset-${key}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
